import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Camera, Loader2, X, CheckCircle, AlertCircle, SwitchCamera } from 'lucide-react';

// Eruda mobile console - uncomment for debugging:
// import eruda from 'eruda';
// if (typeof window !== 'undefined') { eruda.init(); }
import ProductExtractionResults from '../../components/survey/ProductExtractionResults';
import PhotoValidationModal from '../../components/survey/PhotoValidationModal';
import { PhotoExtractionStatus } from '../../components/survey/PhotoExtractionStatus';
import { useClarityTracking } from '../../hooks/useClarityTracking';
import {
  adoptUploadedImage,
  hasSessionImagesDiverged,
  isImageFinal,
  mergeSessionImages,
} from './photoUploadSessionSync';

const API_URL = import.meta.env.VITE_API_URL || '';
// Mirrors DesktopQRUpload's own cap: 900 * 2s = 30 minutes of polling for a real result before
// giving up. The respondent's photos are already uploaded either way — this only bounds how long
// the page keeps asking the server whether extraction has finished.
const MAX_POLL_COUNT = 900;
const POLL_TIMEOUT_MESSAGE =
  'Analysis is taking longer than usual. You can continue — the results will appear on the researcher page.';
const REMOVE_FAILED_MESSAGE = 'Could not remove that photo. Please try again.';
const REQUEST_TIMEOUT_MS = 90_000;
const UPLOAD_TIMEOUT_MESSAGE =
  'That photo took too long to upload — please check your connection and try again.';
const isTimeout = (err: unknown) => err instanceof DOMException && err.name === 'TimeoutError';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed' | 'uploading';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
  rejection_reason?: string;
}

interface SessionData {
  survey_id: number;
  session_id: number;
  question_id: string;
  images: UploadedImage[];
}

interface HealthError {
  type: 'network' | 'backend' | 'session' | 'unknown' | 'camera';
  message: string;
  suggestion: string;
}

// Compress image using canvas if it's larger than threshold
const compressImage = async (
  file: File,
  maxSizeMB: number = 2,
  maxDimension: number = 2048,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    // If file is small enough, just convert to base64
    if (file.size <= maxSizeMB * 1024 * 1024) {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
      return;
    }

    // Compress using canvas
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      try {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;

        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }

        canvas.width = width;
        canvas.height = height;

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        // Start with high quality, reduce if still too large
        let quality = 0.85;
        let result = canvas.toDataURL('image/jpeg', quality);

        // If still too large, reduce quality further
        while (result.length > maxSizeMB * 1024 * 1024 * 1.37 && quality > 0.5) {
          quality -= 0.1;
          result = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(result);
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(img.src);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

const MobilePhotoUpload: React.FC = () => {
  const { uploadToken } = useParams<{ uploadToken: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const { trackEvent, setTag } = useClarityTracking();

  const [session, setSession] = useState<SessionData | null>(null);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Unlike `error` (auto-cleared after 5 s), a notice stays until the respondent's next action.
  const [notice, setNotice] = useState<string | null>(null);
  // Bumped when a new photo lands so the status poll restarts after it gave up at MAX_POLL_COUNT.
  const [pollEpoch, setPollEpoch] = useState(0);
  const [isExpired, setIsExpired] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set());
  const [validationModal, setValidationModal] = useState<{
    isOpen: boolean;
    error: string;
    s3Key: string;
  }>({ isOpen: false, error: '', s3Key: '' });
  const [healthStatus, setHealthStatus] = useState<'checking' | 'healthy' | 'error'>('checking');
  const [healthError, setHealthError] = useState<HealthError | null>(null);
  const [showReloadWarning, setShowReloadWarning] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [cameraPermission, setCameraPermission] = useState<
    'unknown' | 'granted' | 'denied' | 'prompt'
  >('unknown');
  const [isCompressing, setIsCompressing] = useState(false);

  // In-browser camera state (prevents Chrome from killing tab)
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Abort any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Revoke all object URLs
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Clarity tracking - track component mount (absence = JS blocked)
  useEffect(() => {
    trackEvent('mobile_upload_mounted');
    setTag('upload_token', uploadToken?.substring(0, 8) || 'unknown');
    setTag('browser', navigator.userAgent.substring(0, 100));
  }, []);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Persistent debug logging (survives page reload)
  const debugLog = useCallback((msg: string) => {
    const logs = JSON.parse(localStorage.getItem('upload_debug_log') || '[]');
    logs.push(`${new Date().toISOString().slice(11, 23)} ${msg}`);
    // Keep only last 50 logs
    if (logs.length > 50) logs.shift();
    localStorage.setItem('upload_debug_log', JSON.stringify(logs));
    console.log('[Upload]', msg);
  }, []);

  // Track health errors in Clarity for debugging
  useEffect(() => {
    if (healthError) {
      trackEvent(`mobile_upload_health_error_${healthError.type}`);
      setTag('health_error_type', healthError.type);
      setTag('health_error_message', healthError.message.substring(0, 50));
    }
  }, [healthError]);

  // Show previous logs on mount (for debugging)
  useEffect(() => {
    const logs = JSON.parse(localStorage.getItem('upload_debug_log') || '[]');
    if (logs.length > 0) {
      console.log('[Upload] === Previous session logs ===');
      logs.forEach((log: string) => console.log(log));
      console.log('[Upload] === End previous logs ===');
    }
    debugLog('Component mounted');
    // Clear any old camera pending flags
    localStorage.removeItem('camera_pending');
  }, [debugLog]);

  // Track page reloads to detect Samsung Internet caching bug
  useEffect(() => {
    if (!uploadToken) return;

    const reloadKey = `reload_count_${uploadToken}`;
    const currentCount = parseInt(sessionStorage.getItem(reloadKey) || '0', 10);
    const newCount = currentCount + 1;
    sessionStorage.setItem(reloadKey, String(newCount));
    debugLog(`Page load #${newCount} for token ${uploadToken.slice(0, 8)}`);

    // Track reload count in Clarity
    setTag('reload_count', String(newCount));

    // Show warning after 5+ reloads of same session
    if (newCount >= 5) {
      setShowReloadWarning(true);
      trackEvent('mobile_upload_reload_warning_shown');
    }
  }, [uploadToken]);

  // Check camera permission on mount
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
            setCameraPermission(result.state as 'granted' | 'denied' | 'prompt');
            result.addEventListener('change', () => {
              setCameraPermission(result.state as 'granted' | 'denied' | 'prompt');
            });
            return;
          } catch {
            // Permissions API doesn't support camera query
          }
        }
        setCameraPermission('prompt');
      } catch {
        setCameraPermission('prompt');
      }
    };
    checkCameraPermission();
  }, []);

  // Network change listener - auto-retry when connection recovers
  useEffect(() => {
    const handleOnline = () => {
      // Network recovered - retry health checks if we were in error state
      if (healthStatus === 'error') {
        setIsRetrying(true);
        setHealthError(null);
        setHealthStatus('checking');
        runHealthChecks().then((healthy) => {
          setIsRetrying(false);
          if (healthy) {
            validateSession();
          }
        });
      }
    };

    const handleOffline = () => {
      setHealthError({
        type: 'network',
        message: 'You appear to be offline',
        suggestion: 'Check your internet connection and try again',
      });
      setHealthStatus('error');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [healthStatus]);

  // Page visibility change handler - DISABLED for now due to issues with camera switching
  // TODO: Re-enable after fixing race condition with image state updates
  /*
  useEffect(() => {
    let lastHiddenTime: number | null = null;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Page going to background - record timestamp
        lastHiddenTime = Date.now();
      } else {
        // Page becoming visible again
        // Only process if we actually recorded a hidden time (not first load)
        if (lastHiddenTime === null) return;

        const hiddenDuration = Date.now() - lastHiddenTime;
        lastHiddenTime = null; // Reset for next cycle

        // If page was hidden for more than 30 seconds, re-validate session
        // (30s is long enough to rule out quick camera switches)
        if (hiddenDuration > 30000 && session && !isExpired && !isDone) {
          try {
            // Re-fetch session status to check for updates and verify connection
            const res = await fetch(`${API_URL}/api/photo-upload/session/${uploadToken}/status`, {
              credentials: 'include'
            });

            if (!res.ok) throw new Error('Session check failed');
            const data = await res.json();

            if (data.expired) {
              setIsExpired(true);
              setError('Session expired while you were away. Please scan the QR code again.');
              return;
            }

            // Sync images from server - this catches uploads that completed while suspended
            const serverImages: UploadedImage[] = data.images || [];

            // Check for stuck "uploading" placeholders that didn't complete
            const stuckUploads = images.filter(img =>
              img.extraction_status === 'uploading' &&
              !serverImages.some(si => si.s3_key === img.s3_key)
            );

            if (stuckUploads.length > 0) {
              // Remove stuck uploads and show message
              setImages(prevImages => prevImages.filter(img =>
                img.extraction_status !== 'uploading' ||
                serverImages.some(si => si.s3_key === img.s3_key)
              ));
              setUploadingKeys(new Set()); // Clear uploading state
              setError('Some uploads were interrupted. Please try again.');
            } else if (serverImages.length > images.filter(i => i.extraction_status !== 'uploading').length) {
              // Server has more images than we do - sync them
              // Preserve local previews for images we already have
              const mergedImages = serverImages.map(si => {
                const existing = images.find(i => i.s3_key === si.s3_key);
                return existing ? { ...si, localPreview: existing.localPreview } : si;
              });
              setImages(mergedImages);
            }
          } catch {
            // Connection issue - show recoverable error
            setError('Connection lost. Checking status...');
            // Try health check to see if we can recover
            const healthy = await runHealthChecks();
            if (healthy) {
              setError(null);
              validateSession();
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [uploadToken, session, isExpired, isDone, images]);
  */

  // Helper function with retry logic for stale connections
  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    retries = 1,
  ): Promise<Response> => {
    try {
      const response = await fetch(url, {
        ...options,
        signal: options.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      return response;
    } catch (err) {
      if (isTimeout(err)) throw err; // a timed-out upload may have landed; a retry would duplicate it
      if (retries > 0) {
        // Wait 500ms and retry with fresh connection
        await new Promise((r) => setTimeout(r, 500));
        return fetchWithRetry(url, options, retries - 1);
      }
      throw err;
    }
  };

  // Run health checks before session validation
  const runHealthChecks = async (): Promise<boolean> => {
    setHealthStatus('checking');
    setHealthError(null);

    try {
      // Check 1: Backend reachable (simple health ping)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      let pingResponse: Response;
      try {
        pingResponse = await fetchWithRetry(
          `${API_URL}/api/health`,
          { method: 'GET', signal: controller.signal },
          1, // 1 retry for stale connections
        );
        clearTimeout(timeoutId);
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        // Network error or timeout
        if (fetchErr instanceof Error && fetchErr.name === 'AbortError') {
          setHealthError({
            type: 'network',
            message: 'Connection timed out',
            suggestion:
              'Your network may be slow or unstable. Try:\n• Using mobile data instead of WiFi\n• Moving to an area with better signal',
          });
        } else {
          setHealthError({
            type: 'network',
            message: 'Network request blocked',
            suggestion:
              'Your network or browser may be blocking this page. Try:\n• Using mobile data instead of WiFi\n• Disabling VPN or firewall\n• Using a different browser',
          });
        }
        setHealthStatus('error');
        return false;
      }

      if (!pingResponse.ok) {
        setHealthError({
          type: 'backend',
          message: 'Cannot connect to server',
          suggestion: 'Check your internet connection or try again in a moment',
        });
        setHealthStatus('error');
        return false;
      }

      // Check 2: Session endpoint reachable with credentials (CORS check)
      const controller2 = new AbortController();
      const timeoutId2 = setTimeout(() => controller2.abort(), 5000);

      try {
        const credentialsTest = await fetchWithRetry(
          `${API_URL}/api/photo-upload/session/${uploadToken}/status`,
          { credentials: 'include', signal: controller2.signal },
          1, // 1 retry for stale connections
        );
        clearTimeout(timeoutId2);

        // 404 is fine (invalid token handled in validateSession)
        // Other non-ok responses that aren't 404 indicate CORS/blocking issues
        if (!credentialsTest.ok && credentialsTest.status !== 404) {
          if (credentialsTest.status >= 500) {
            setHealthError({
              type: 'backend',
              message: 'Server is temporarily unavailable',
              suggestion: 'Please try again in a moment',
            });
            setHealthStatus('error');
            return false;
          }
        }
      } catch (fetchErr) {
        clearTimeout(timeoutId2);
        // CORS or network blocking
        setHealthError({
          type: 'network',
          message: 'Connection blocked by security settings',
          suggestion: 'Try using a different browser or disable content blockers',
        });
        setHealthStatus('error');
        return false;
      }

      setHealthStatus('healthy');
      return true;
    } catch (err) {
      setHealthError({
        type: 'unknown',
        message: 'Something went wrong',
        suggestion: 'Please scan the QR code again from your computer to get a fresh link',
      });
      setHealthStatus('error');
      return false;
    }
  };

  useEffect(() => {
    if (uploadToken) {
      // Run health checks first, then validate session if healthy
      runHealthChecks().then((healthy) => {
        if (healthy) {
          validateSession();
        } else {
          setIsLoading(false);
        }
      });
    }
  }, [uploadToken]);

  const validateSession = async () => {
    try {
      const response = await fetch(`${API_URL}/api/photo-upload/session/${uploadToken}/status`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Session not found');
      }

      const data = await response.json();

      if (data.expired) {
        setIsExpired(true);
        setError('This upload session has expired. Please scan the QR code again.');
      } else {
        setSession({
          survey_id: 0,
          session_id: data.session_id,
          question_id: data.question_id,
          images: data.images || [],
        });
        setImages(data.images || []);
      }
    } catch (err) {
      setError('Invalid or expired upload session');
      setIsExpired(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Refs mirroring the latest `images` so the long-lived setInterval poll below always reads
  // current state instead of the closure from the render that started it.
  const imagesRef = useRef<UploadedImage[]>(images);
  // Bumped on every local list change the server has not seen yet (an upload landing, a
  // removal), so a /status response that was in flight across that change is discarded as stale.
  const listGenRef = useRef(0);
  // Keys the respondent removed on this page. A listing may still show one (an in-flight DELETE, or a
  // rejected upload's background S3 put landing after the delete) and must never put it back.
  const removedKeysRef = useRef<Set<string>>(new Set());
  // Keys this page uploaded itself, kept through a listing that lacks them (S3 lag on a rejected
  // upload) — retired the first time a listing shows them, so a desktop-side delete still syncs.
  const ownUploadsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  // Poll the upload session while any photo has not reached a real extraction result yet. The
  // photo itself already uploaded — this only waits for the AI's product/rejection verdict, and
  // mirrors DesktopQRUpload's own startPolling so both surfaces converge on the same "real
  // result" definition. Deliberately gated on a derived boolean rather than
  // `images` itself: the effect only needs to start once (when something becomes unresolved) and
  // stop once (when everything resolves), not tear down and re-create its interval on every tick.
  //
  // `isDone`/`isExpired` are also guarded and in the dep list: once the respondent has synced
  // (Done) or the session has expired, there is nothing left to poll for and the page has moved
  // to a terminal screen.
  const hasUnresolvedImages = images.some((img) => !isImageFinal(img));
  useEffect(() => {
    if (!uploadToken || !hasUnresolvedImages || isDone || isExpired) return;

    let cancelled = false;
    let pollCount = 0;

    const pollInterval = setInterval(async () => {
      pollCount++;
      if (pollCount >= MAX_POLL_COUNT) {
        clearInterval(pollInterval);
        setNotice(POLL_TIMEOUT_MESSAGE);
        return;
      }

      const generation = listGenRef.current;
      try {
        const response = await fetch(`${API_URL}/api/photo-upload/session/${uploadToken}/status`, {
          credentials: 'include',
          signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        });
        if (cancelled || !response.ok) return;

        const data = await response.json();
        if (cancelled || generation !== listGenRef.current) return;

        // Mirrors DesktopQRUpload's startPolling (~229-238): the session itself can expire
        // mid-poll, independent of any single image's extraction state.
        if (data.expired) {
          clearInterval(pollInterval);
          setIsExpired(true);
          return;
        }

        if (!Array.isArray(data.images)) return;

        const currentImages = imagesRef.current;
        for (const img of data.images as UploadedImage[]) ownUploadsRef.current.delete(img.s3_key);
        const listed = data.images.filter(
          (img: UploadedImage) => !removedKeysRef.current.has(img.s3_key),
        );
        const next = mergeSessionImages<UploadedImage>(listed, currentImages, {
          keepInFlight: true,
          keepKeys: ownUploadsRef.current,
        });
        if (hasSessionImagesDiverged(next, currentImages)) setImages(next);
      } catch (err) {
        console.error('Extraction status poll error:', err);
      }
    }, 2000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
    };
  }, [uploadToken, hasUnresolvedImages, isDone, isExpired, pollEpoch]);

  const handleCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    debugLog('handleCapture called (file input fallback)');
    localStorage.removeItem('camera_pending');

    try {
      const fileList = event.target.files;
      debugLog(`Files: ${fileList?.length || 0}`);

      if (!fileList || fileList.length === 0) {
        debugLog('No files selected');
        return;
      }

      // IMPORTANT: Copy file BEFORE resetting input, as reset clears the FileList
      const file = fileList[0];
      debugLog(`File: ${file.name} Size: ${file.size} Type: ${file.type}`);

      // Reset file input to allow re-selecting the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (!session || !uploadToken) {
        debugLog('No session or token');
        setError('Session not ready. Please wait and try again.');
        return;
      }

      if (file.size > 15 * 1024 * 1024) {
        debugLog('File too large');
        setError('File size exceeds 15MB limit');
        return;
      }

      setError(null);
      debugLog('Starting upload process');

      // Create preview URL
      const localPreview = URL.createObjectURL(file);
      objectUrlsRef.current.push(localPreview);

      // Generate temporary key
      const tempKey = `uploading-${Date.now()}`;

      // Create placeholder image
      const placeholderImage: UploadedImage = {
        s3_key: tempKey,
        url: '',
        extracted_products: [],
        extraction_status: 'uploading',
        upload_timestamp: new Date().toISOString(),
        localPreview,
      };

      // Update state immediately to show preview
      setImages((prevImages) => [...prevImages, placeholderImage]);
      setUploadingKeys((prev) => new Set(prev).add(tempKey));
      setNotice(null);

      // Upload in background
      const doUpload = async () => {
        debugLog('doUpload started');
        try {
          // Compress large images (>2MB) to speed up upload and reduce timeouts
          // Shows "Compressing..." state for large files
          let base64: string;
          if (file.size > 2 * 1024 * 1024) {
            debugLog('Compressing large image...');
            setIsCompressing(true);
            try {
              base64 = await compressImage(file, 2, 2048);
              debugLog(`Compression done, size: ${base64.length}`);
            } finally {
              setIsCompressing(false);
            }
          } else {
            // Small file - just convert to base64
            debugLog('Reading small file as base64...');
            base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(file);
            });
            debugLog(`Base64 ready, size: ${base64.length}`);
          }

          debugLog('Sending to server...');
          // Use fetchWithRetry for uploads to handle flaky mobile connections
          const response = await fetchWithRetry(
            `${API_URL}/api/photo-upload/upload`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                upload_token: uploadToken,
                image_base64: base64,
              }),
              credentials: 'include',
            },
            2, // 2 retries for upload requests (more important than health checks)
          );

          debugLog(`Response status: ${response.status}`);
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            debugLog(`Error response: ${JSON.stringify(errorData)}`);
            throw new Error(errorData.detail || 'Upload failed');
          }

          const result = await response.json();
          debugLog(`Success! s3_key: ${result.image?.s3_key}`);

          const uploadedImage: UploadedImage = {
            ...result.image,
            localPreview,
          };

          listGenRef.current += 1;

          ownUploadsRef.current.add(uploadedImage.s3_key);

          setPollEpoch((epoch) => epoch + 1);

          setImages((prevImages) => adoptUploadedImage(prevImages, tempKey, uploadedImage));
          debugLog('Image state updated');

          // Track successful upload
          trackEvent('mobile_upload_photo_success');

          // Check for validation failure
          if (uploadedImage.extraction_status === 'failed' && !result.image?.show_text_fallback) {
            setValidationModal({
              isOpen: true,
              error: result.image?.rejection_reason || 'Image could not be processed.',
              s3Key: uploadedImage.s3_key,
            });
          }
        } catch (err) {
          debugLog(`FAILED: ${err instanceof Error ? err.message : String(err)}`);
          // Remove placeholder on error
          URL.revokeObjectURL(localPreview);
          objectUrlsRef.current = objectUrlsRef.current.filter((u) => u !== localPreview);
          setImages((prevImages) => prevImages.filter((img) => img.s3_key !== tempKey));
          setError(
            isTimeout(err)
              ? UPLOAD_TIMEOUT_MESSAGE
              : err instanceof Error
                ? err.message
                : 'Upload failed',
          );
        } finally {
          setUploadingKeys((prev) => {
            const next = new Set(prev);
            next.delete(tempKey);
            return next;
          });
        }
      };

      doUpload();
    } catch (err) {
      debugLog(`handleCapture error: ${err instanceof Error ? err.message : String(err)}`);
      setError('Failed to process photo. Please try again.');
    }
  };

  // Start in-browser camera (prevents Chrome from killing tab)
  const startCamera = async () => {
    debugLog('Starting in-browser camera');
    setShowCamera(true); // Show camera view immediately
    setCameraReady(false);

    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        debugLog('getUserMedia not supported - falling back to file input');
        setShowCamera(false);
        // Fallback to file input
        fileInputRef.current?.click();
        return;
      }

      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      debugLog(`Requesting camera with facingMode: ${facingMode}`);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      debugLog(`Got stream with ${stream.getVideoTracks().length} video tracks`);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        debugLog('Set video srcObject, waiting for play...');

        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          debugLog(
            `Video metadata loaded: ${videoRef.current?.videoWidth}x${videoRef.current?.videoHeight}`,
          );
        };

        await videoRef.current.play();
        debugLog('Video playing');
        setCameraReady(true);
      } else {
        debugLog('ERROR: videoRef.current is null');
        throw new Error('Video element not found');
      }
    } catch (err) {
      debugLog(
        `Camera error: ${err instanceof Error ? `${err.name}: ${err.message}` : String(err)}`,
      );
      setShowCamera(false);

      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError(
            'Camera access denied. Please allow camera permission in your browser settings.',
          );
          setCameraPermission('denied');
        } else if (err.name === 'NotFoundError') {
          setError('No camera found on this device.');
        } else if (err.name === 'NotReadableError') {
          setError(
            'Camera is in use by another app. Please close other camera apps and try again.',
          );
        } else if (err.name === 'OverconstrainedError') {
          // Try again with simpler constraints
          debugLog('Retrying with simpler constraints');
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            streamRef.current = stream;
            if (videoRef.current) {
              videoRef.current.srcObject = stream;
              await videoRef.current.play();
              setCameraReady(true);
              setShowCamera(true);
              return;
            }
          } catch (retryErr) {
            debugLog(`Retry also failed: ${retryErr}`);
            setError('Could not access camera. Please try again.');
          }
        } else {
          setError(`Camera error: ${err.message}`);
        }
      } else {
        setError('Could not access camera. Please try again.');
      }
    }
  };

  // Stop camera and close view
  const stopCamera = () => {
    debugLog('Stopping camera');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setShowCamera(false);
  };

  // Switch between front and back camera
  const switchCamera = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    debugLog(`Switching to ${newMode} camera`);

    // Restart camera with new facing mode
    if (showCamera) {
      setCameraReady(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: newMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setCameraReady(true);
        }
      } catch (err) {
        debugLog(`Camera switch error: ${err}`);
        setError('Could not switch camera');
      }
    }
  };

  // Capture photo from video stream
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current || !cameraReady) {
      debugLog('Camera not ready for capture');
      return;
    }

    debugLog('Capturing photo from stream');
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      setError('Could not capture photo');
      return;
    }

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0);

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setError('Could not capture photo');
          return;
        }

        // Create a File object from the blob
        const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
        debugLog(`Captured photo: ${file.size} bytes`);

        // Stop camera
        stopCamera();

        // Process the captured file using existing upload logic
        processFile(file);
      },
      'image/jpeg',
      0.9,
    );
  };

  // Process a file (shared between camera capture and file input)
  const processFile = (file: File) => {
    if (!session || !uploadToken) {
      setError('Session not ready. Please wait and try again.');
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      setError('File size exceeds 15MB limit');
      return;
    }

    setError(null);
    debugLog('Processing captured file');

    // Create preview URL
    const localPreview = URL.createObjectURL(file);
    objectUrlsRef.current.push(localPreview);

    // Generate temporary key
    const tempKey = `uploading-${Date.now()}`;

    // Create placeholder image
    const placeholderImage: UploadedImage = {
      s3_key: tempKey,
      url: '',
      extracted_products: [],
      extraction_status: 'uploading',
      upload_timestamp: new Date().toISOString(),
      localPreview,
    };

    // Update state immediately to show preview
    setImages((prevImages) => [...prevImages, placeholderImage]);
    setUploadingKeys((prev) => new Set(prev).add(tempKey));
    setNotice(null);

    // Upload in background
    const doUpload = async () => {
      debugLog('doUpload started (from camera capture)');
      try {
        let base64: string;
        if (file.size > 2 * 1024 * 1024) {
          debugLog('Compressing large image...');
          setIsCompressing(true);
          try {
            base64 = await compressImage(file, 2, 2048);
            debugLog(`Compression done, size: ${base64.length}`);
          } finally {
            setIsCompressing(false);
          }
        } else {
          debugLog('Reading file as base64...');
          base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
          });
          debugLog(`Base64 ready, size: ${base64.length}`);
        }

        debugLog('Sending to server...');
        const response = await fetchWithRetry(
          `${API_URL}/api/photo-upload/upload`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              upload_token: uploadToken,
              image_base64: base64,
            }),
            credentials: 'include',
          },
          2,
        );

        debugLog(`Response status: ${response.status}`);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          debugLog(`Error response: ${JSON.stringify(errorData)}`);
          throw new Error(errorData.detail || 'Upload failed');
        }

        const result = await response.json();
        debugLog(`Success! s3_key: ${result.image?.s3_key}`);

        const uploadedImage: UploadedImage = {
          ...result.image,
          localPreview,
        };

        listGenRef.current += 1;

        ownUploadsRef.current.add(uploadedImage.s3_key);

        setPollEpoch((epoch) => epoch + 1);

        setImages((prevImages) => adoptUploadedImage(prevImages, tempKey, uploadedImage));
        debugLog('Image state updated');

        trackEvent('mobile_upload_photo_success');

        if (uploadedImage.extraction_status === 'failed' && !result.image?.show_text_fallback) {
          setValidationModal({
            isOpen: true,
            error: result.image?.rejection_reason || 'Image could not be processed.',
            s3Key: uploadedImage.s3_key,
          });
        }
      } catch (err) {
        debugLog(`FAILED: ${err instanceof Error ? err.message : String(err)}`);
        URL.revokeObjectURL(localPreview);
        objectUrlsRef.current = objectUrlsRef.current.filter((u) => u !== localPreview);
        setImages((prevImages) => prevImages.filter((img) => img.s3_key !== tempKey));
        setError(
          isTimeout(err)
            ? UPLOAD_TIMEOUT_MESSAGE
            : err instanceof Error
              ? err.message
              : 'Upload failed',
        );
      } finally {
        setUploadingKeys((prev) => {
          const next = new Set(prev);
          next.delete(tempKey);
          return next;
        });
      }
    };

    doUpload();
  };

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleRemoveImage = async (s3Key: string) => {
    const removedIndex = imagesRef.current.findIndex((img) => img.s3_key === s3Key);
    const removedImage = imagesRef.current[removedIndex];
    // The preview blob is revoked only once the removal is final — a restored tile needs it.
    const releasePreview = () => {
      if (!removedImage?.localPreview) return;
      URL.revokeObjectURL(removedImage.localPreview);
      objectUrlsRef.current = objectUrlsRef.current.filter((u) => u !== removedImage.localPreview);
    };
    listGenRef.current += 1;
    removedKeysRef.current.add(s3Key);
    setImages((prev) => prev.filter((img) => img.s3_key !== s3Key));

    if (!uploadToken || !removedImage) {
      releasePreview();
      return;
    }

    // The poll treats the session as authoritative, so a removal that never reached the server
    // would simply reappear on the next tick — put it back at its index and say so (as the
    // desktop does since frontend#578).
    const failRemoval = () => {
      listGenRef.current += 1;
      removedKeysRef.current.delete(s3Key);
      setImages((prev) => {
        const restored = prev.filter((img) => img.s3_key !== s3Key);
        restored.splice(Math.min(removedIndex, restored.length), 0, removedImage);
        return restored;
      });
      setNotice(REMOVE_FAILED_MESSAGE);
    };

    try {
      const response = await fetch(
        `${API_URL}/api/photo-upload/session/${uploadToken}/image/${encodeURIComponent(s3Key)}`,
        { method: 'DELETE', credentials: 'include' },
      );
      // `s3_deleted: false` = S3 disabled or the delete errored. S3's delete is idempotent, so `true`
      // does not prove the object is gone — which is why removed keys are never re-adopted above.
      const body = response.ok ? await response.json().catch(() => ({})) : {};
      if (response.ok && body.s3_deleted !== false) {
        listGenRef.current += 1;
        ownUploadsRef.current.delete(s3Key);
        releasePreview();
        setNotice(null);
      } else failRemoval();
    } catch {
      failRemoval();
    }
  };

  const handleRetake = async () => {
    const s3Key = validationModal.s3Key;
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
    await handleRemoveImage(s3Key);
    // Trigger camera immediately after modal closes
    setTimeout(() => {
      debugLog('Retake - starting camera');
      startCamera();
    }, 100);
  };

  const handleContinueAnyway = () => {
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
  };

  const handleDone = async () => {
    if (!uploadToken || images.length === 0) {
      setIsDone(true);
      return;
    }

    setIsSyncing(true);

    try {
      const response = await fetch(`${API_URL}/api/photo-upload/session/${uploadToken}/complete`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to sync with desktop');
      }

      const result = await response.json();

      if (result.success) {
        setIsDone(true);
      } else {
        throw new Error('Sync failed');
      }
    } catch (err) {
      setError('Failed to sync photos. Your photos are saved - please check your computer.');
      // Still allow completing after error
      setTimeout(() => setIsDone(true), 2000);
    } finally {
      setIsSyncing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07E5D1]" />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2">Session Expired</h1>
          <p className="text-gray-600">
            {error ||
              'This upload session has expired. Please scan the QR code again from your computer.'}
          </p>
        </div>
      </div>
    );
  }

  if (healthError) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2">{healthError.message}</h1>
          <p className="text-gray-600 mb-6 whitespace-pre-line">{healthError.suggestion}</p>
          {isRetrying ? (
            <div className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
              <span className="text-gray-700 font-medium">Reconnecting...</span>
            </div>
          ) : (
            <button
              onClick={() => {
                setIsRetrying(true);
                setHealthError(null);
                setHealthStatus('checking');
                setIsLoading(true);
                runHealthChecks().then((healthy) => {
                  setIsRetrying(false);
                  if (healthy) {
                    validateSession();
                  } else {
                    setIsLoading(false);
                  }
                });
              }}
              className="px-6 py-3 bg-[#07E5D1] text-white rounded-lg font-medium hover:bg-[#06D4C2] transition-colors"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isDone) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <CheckCircle className="w-16 h-16 text-[#07E5D1] mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800 mb-2">Photos Synced!</h1>
          <p className="text-gray-600 mb-2">
            {images.length} photo{images.length !== 1 ? 's have' : ' has'} been synced to your
            computer.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-emerald-700">Your desktop has received all photos.</p>
          </div>
          <p className="text-sm text-gray-500">
            You can close this page and return to your computer to continue the survey.
          </p>
        </div>
      </div>
    );
  }

  // Only disable Done during active upload/compression - extraction happens in background
  const isProcessing = uploadingKeys.size > 0 || isSyncing || isCompressing;

  return (
    <div className="min-h-screen bg-[#FEFEFE]">
      {/* Syncing overlay */}
      {isSyncing && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#07E5D1] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Syncing with Desktop</h2>
            <p className="text-gray-600">
              Confirming {images.length} photo{images.length !== 1 ? 's' : ''} uploaded...
            </p>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-gray-100 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-800">Photo Upload</h1>
          {images.length > 0 && (
            <button
              onClick={handleDone}
              disabled={isProcessing}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors
                         ${
                           isProcessing
                             ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                             : 'bg-[#07E5D1] text-white hover:bg-[#06D4C2]'
                         }`}
            >
              {isSyncing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Syncing...
                </>
              ) : isCompressing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Compressing...
                </>
              ) : uploadingKeys.size > 0 ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Done
                </>
              )}
            </button>
          )}
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Camera permission denied warning */}
        {cameraPermission === 'denied' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-medium mb-1">Camera access needed</p>
            <p className="text-sm text-blue-700 mb-2">
              To take photos, please allow camera access in your browser settings.
            </p>
            <p className="text-xs text-blue-600">
              You can still select existing photos from your gallery by tapping the button below.
            </p>
          </div>
        )}

        {/* Reload warning for users who have refreshed many times */}
        {showReloadWarning && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 font-medium mb-1">Having trouble?</p>
            <p className="text-sm text-amber-700">
              If photos aren't uploading, try scanning a fresh QR code from your computer.
            </p>
            <button
              onClick={() => setShowReloadWarning(false)}
              className="text-xs text-amber-600 underline mt-2"
            >
              Dismiss
            </button>
          </div>
        )}

        <input
          id="mobile-camera-input"
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleCapture}
          className="hidden"
        />

        {images.length === 0 ? (
          <button
            type="button"
            onClick={() => {
              debugLog('Take Photo button clicked');
              startCamera();
            }}
            disabled={!session}
            className={`w-full py-12 border-2 border-dashed rounded-xl flex flex-col
                       items-center justify-center gap-4 transition-colors
                       ${
                         session
                           ? 'border-[#07E5D1] bg-[#E8FCFA] hover:bg-[#d0f9f5] cursor-pointer'
                           : 'border-gray-300 bg-gray-100 cursor-wait'
                       }`}
          >
            {session ? (
              <>
                <Camera className="w-12 h-12 text-[#07E5D1]" />
                <span className="text-xl font-medium text-[#06D4C2]">Take Photo</span>
                <span className="text-sm text-[#07E5D1]">Tap to capture your pantry or fridge</span>
              </>
            ) : (
              <>
                <Loader2 className="w-12 h-12 text-gray-400 animate-spin" />
                <span className="text-xl font-medium text-gray-500">Loading session...</span>
              </>
            )}
          </button>
        ) : (
          <div className="space-y-4">
            {images.map((image) => (
              <div
                key={image.s3_key}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm"
              >
                <div className="aspect-video relative bg-gray-100">
                  <img
                    src={image.localPreview || `${API_URL}${image.url}`}
                    alt="Uploaded photo"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(image.s3_key)}
                    disabled={uploadingKeys.has(image.s3_key)}
                    className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full
                               hover:bg-red-600 transition-colors shadow-md disabled:opacity-50"
                  >
                    <X className="w-5 h-5" />
                  </button>

                  {/* Per-image uploading/compressing overlay */}
                  {uploadingKeys.has(image.s3_key) && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="bg-white rounded-lg px-4 py-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium">
                          {isCompressing ? 'Compressing...' : 'Uploading...'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-3 pt-2">
                  <PhotoExtractionStatus image={image} />
                </div>

                {image.extraction_status === 'completed' &&
                  image.extracted_products?.length > 0 && (
                    <ProductExtractionResults
                      products={image.extracted_products}
                      showImage={false}
                    />
                  )}
              </div>
            ))}

            <button
              type="button"
              onClick={() => {
                if (images.length < 3) {
                  debugLog('Add Another Photo clicked');
                  startCamera();
                }
              }}
              disabled={images.length >= 3}
              className={`w-full py-4 border-2 border-dashed rounded-xl transition-colors
                         flex items-center justify-center gap-2
                         ${
                           images.length >= 3
                             ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                             : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#07E5D1] cursor-pointer'
                         }`}
            >
              <Camera className="w-5 h-5 text-gray-500" />
              <span className="text-gray-600 font-medium">Add Another Photo</span>
              {uploadingKeys.size > 0 && (
                <span className="text-xs text-[#07E5D1]">({uploadingKeys.size} uploading)</span>
              )}
            </button>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600 text-center bg-red-50 p-3 rounded-lg">{error}</p>
        )}
        {notice && (
          <p
            className="text-sm text-amber-800 text-center bg-amber-50 p-3 rounded-lg"
            role="status"
          >
            {notice}
          </p>
        )}

        <p className="text-xs text-gray-500 text-center">
          Photos are securely uploaded and analyzed for products
        </p>
      </main>

      <PhotoValidationModal
        isOpen={validationModal.isOpen}
        error={validationModal.error}
        onRetake={handleRetake}
        onContinueAnyway={handleContinueAnyway}
      />

      {/* Hidden canvas for capturing photos */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Fullscreen in-browser camera view */}
      {showCamera && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          {/* Camera preview */}
          <div className="flex-1 relative overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />

            {/* Loading overlay */}
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black">
                <div className="text-center text-white">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" />
                  <p>Starting camera...</p>
                </div>
              </div>
            )}
          </div>

          {/* Camera controls */}
          <div className="bg-black/90 p-6 pb-10 flex items-center justify-center gap-8">
            {/* Cancel button */}
            <button
              onClick={stopCamera}
              className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center"
            >
              <X className="w-7 h-7 text-white" />
            </button>

            {/* Capture button */}
            <button
              onClick={capturePhoto}
              disabled={!cameraReady}
              className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center
                         ${cameraReady ? 'bg-white' : 'bg-white/50'}`}
            >
              <div
                className={`w-16 h-16 rounded-full ${cameraReady ? 'bg-[#07E5D1]' : 'bg-gray-400'}`}
              />
            </button>

            {/* Switch camera button */}
            <button
              onClick={switchCamera}
              disabled={!cameraReady}
              className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center"
            >
              <SwitchCamera className="w-7 h-7 text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePhotoUpload;
